import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search, FileText, DollarSign } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

interface MedicalProfile {
  id: number;
  nameKey: string;
  descriptionKey: string;
  testsKey: string;
  price: number;
  preparationKey: string;
}

const MedicalProfiles = () => {
  const { t } = useContent();
  const [searchQuery, setSearchQuery] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);

  const profiles: MedicalProfile[] = [
    {
      id: 1,
      nameKey: 'medicalProfiles.completeHealth.title',
      descriptionKey: 'medicalProfiles.completeHealth.description',
      testsKey: 'medicalProfiles.completeHealth.tests',
      price: 500,
      preparationKey: 'medicalProfiles.preparation.fasting12',
    },
    {
      id: 2,
      nameKey: 'medicalProfiles.diabetes.title',
      descriptionKey: 'medicalProfiles.diabetes.description',
      testsKey: 'medicalProfiles.diabetes.tests',
      price: 400,
      preparationKey: 'medicalProfiles.preparation.fasting12',
    },
    {
      id: 3,
      nameKey: 'medicalProfiles.cardiac.title',
      descriptionKey: 'medicalProfiles.cardiac.description',
      testsKey: 'medicalProfiles.cardiac.tests',
      price: 450,
      preparationKey: 'medicalProfiles.preparation.fasting12',
    },
    {
      id: 4,
      nameKey: 'medicalProfiles.womensHealth.title',
      descriptionKey: 'medicalProfiles.womensHealth.description',
      testsKey: 'medicalProfiles.womensHealth.tests',
      price: 600,
      preparationKey: 'medicalProfiles.preparation.noPreparation',
    },
  ];

  useEffect(() => {
    document.title = `${t('common.medicalProfiles')} - ElhamyLabs`;
  }, [t]);

  const filteredProfiles = profiles.filter((profile) =>
    t(profile.nameKey).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('medicalProfiles.title')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('medicalProfiles.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 bg-light-secondary dark:bg-dark-secondary sticky top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-light-text-secondary dark:text-dark-text-secondary" size={20} />
            <input
              type="text"
              placeholder={t('medicalProfiles.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border border-light-text/10 dark:border-dark-text/10 focus:outline-none focus:ring-2 focus:ring-brand-purple-base dark:focus:ring-brand-orange-base"
            />
          </div>
        </div>
      </section>

      {/* Profiles Grid */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProfiles.map((profile, index) => (
              <ScrollReveal key={profile.id} delay={index * 0.1}>
                <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText size={32} className="text-brand-purple-base dark:text-brand-orange-base" />
                    <h3 className="text-2xl font-bold text-light-text dark:text-dark-text">
                      {t(profile.nameKey)}
                    </h3>
                  </div>
                  <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
                    {t(profile.descriptionKey)}
                  </p>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 text-light-text dark:text-dark-text">{t('common.includes')}:</h4>
                    <ul className="space-y-1">
                      {(t(profile.testsKey, { returnObjects: true }) as string[]).map((test, idx) => (
                        <li key={idx} className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                          • {test}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-1 text-light-text dark:text-dark-text">{t('common.preparation')}:</h4>
                    <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                      {t(profile.preparationKey)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-light-text/10 dark:border-dark-text/10">
                    <div className="flex items-center gap-2">
                      <DollarSign size={20} className="text-brand-orange-base" />
                      <span className="text-2xl font-bold text-brand-orange-base">{profile.price} EGP</span>
                    </div>
                    <Link
                      to="/home-visit-request"
                      className="px-4 py-2 bg-brand-orange-base text-white rounded-lg hover:bg-brand-orange-light transition-colors"
                    >
                      {t('common.book')}
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MedicalProfiles;

